/**
 * Wire message schemas — client→server and server→client.
 *
 * Rules (see wp1-protocol.md):
 *   - All objects use `z.strictObject` (unknown extra keys ⇒ parse error).
 *   - NO `.optional()` anywhere. Absence is modeled as explicit `null`
 *     inside a `z.union([T, z.null()])`.
 *   - Discriminated on `type` (server / client envelope) or `kind` (sim
 *     input), never both on the same union.
 *   - MAX_MESSAGE_BYTES is enforced by the socket layer (WP5), not here.
 */

import { z } from "zod";
import type { Units } from "./brands.js";
import { brandedInt } from "./zod-helpers.js";
import { AURA_POLARITY_SCHEMA, CC_CLASS_SCHEMA } from "./content-schemas.js";
import {
  ABILITY_ID_SCHEMA,
  ABILITY_SLOT_SCHEMA,
  AURA_DEF_ID_SCHEMA,
  ENTITY_ID_SCHEMA,
  HP_SCHEMA,
  MATCH_ID_SCHEMA,
  MODE_ID_SCHEMA,
  CHAMPION_ID_SCHEMA,
  SEAT_INDEX_SCHEMA,
  SEQ_SCHEMA,
  STATE_HASH_SCHEMA,
  TEAM_INDEX_SCHEMA,
  TICK_SCHEMA,
  UNITS_SCHEMA,
} from "./branded-schemas.js";
import {
  DISPLAY_TAG_PATTERN,
  MAX_ABILITY_SLOT,
  MAX_CHAT_PER_SNAPSHOT,
  MAX_CHAT_TEXT_LEN,
  MAX_DISPLAY_NAME_LEN,
  MAX_FACING,
  MAX_LOADOUT_SIZE,
  MIN_DISPLAY_NAME_LEN,
  PROTOCOL_VERSION_PATTERN,
} from "./limits.js";

/**
 * `PROTOCOL_VERSION`'s wire shape (#309): `${WIRE_EPOCH}:${SCHEMA_HASH}` —
 * was a bare `z.number().int()` before #309; see `limits.ts` for the full
 * rationale. Equality-only on both sides of the handshake — never compared
 * for ordering.
 */
const PROTOCOL_VERSION_SCHEMA = z.string().regex(PROTOCOL_VERSION_PATTERN);

/**
 * #323: a player's pre-match LOADOUT — the subset of their champion's ability
 * pool (up to `MAX_LOADOUT_SIZE`) they bring into this match. `null` = "no
 * customization, use the champion's full pool". A present array is a genuine
 * subset choice,
 * validated server-side against the SELECTED champion's pool (membership +
 * exact-size + no-duplicate — see `@pit/content`'s `resolveLoadout`); the
 * schema here only enforces the WIRE-LEVEL shape (1..12 unique ids) since
 * "does this id belong to this champion" needs the content bundle.
 *
 * SLOT-ORDER STATUS (2026-07-19: REVISED — see #417): a loadout here is
 * currently a pure SUBSET selection, never a reorder. Each ability keeps its
 * CONTENT-CANONICAL slot (the position `Champion.abilities` already assigns
 * it — the same slot the existing action-bar/cooldown tables key off); an
 * ability left OUT of the loadout simply renders as an empty slot this
 * match, exactly like a champion who was never authored an ability there.
 *
 * CORRECTION to the original #323 design note: this file used to justify
 * that state by calling player-chosen slot placement a "purely cosmetic
 * reordering benefit." That characterization was WRONG and is retracted —
 * competitive players in comparable ability-bar games routinely reposition
 * abilities for muscle memory, priority, and visibility, and that's a real,
 * material gameplay want, not decoration. The actual reason player-chosen
 * order isn't implemented YET is a genuine engineering cost — it would
 * require either (a) renumbering `Ability.slot` per match, a content-
 * injected field the cast pipeline reads for cooldown/charge/event
 * attribution throughout (`packages/sim/src/cast.ts`), or (b) a separate
 * client-side display-slot-to-ability remap layered on top of whatever
 * canonical/submitted order the sim uses — NOT a judgment that the feature
 * itself is low-value. See #417 for the tracked follow-up to actually build
 * player-controlled slot assignment, and #411 for a related sim/client slot-
 * order desync bug that must be sequenced with it.
 *
 * Array ORDER in this WIRE field is therefore not semantically meaningful
 * beyond "the set of chosen ids"; `resolveLoadout` validates membership, not
 * order (a scrambled submission order is not rejected) — until #417 lands.
 *
 * #411 (fixed): `resolveLoadout`'s OUTPUT — the resolved `abilityIds` that
 * becomes `Entity.abilityIds` — is always CONTENT-CANONICAL pool order,
 * regardless of this field's submission order. Before that fix, a partial
 * loadout's accepted subset was returned verbatim in submission order,
 * which could desync from every client-side slot-order table (all derived
 * straight from content order) the moment a player's pool exceeded
 * `MAX_LOADOUT_SIZE` and they (de)selected abilities in a non-canonical
 * sequence (Afflictor and, as of #540, Warrior — both 13-ability pools).
 */
export const LOADOUT_SCHEMA = z.union([
  z
    .array(ABILITY_ID_SCHEMA)
    .min(1)
    .max(MAX_LOADOUT_SIZE)
    .refine((ids) => new Set(ids).size === ids.length, {
      message: "loadout must not contain duplicate ability ids",
    }),
  z.null(),
]);

export type Loadout = z.infer<typeof LOADOUT_SCHEMA>;

/**
 * #540: the RESOLVED (post-`resolveLoadout`) ability-id list a replay stores
 * (`REPLAY_PLAYER_SCHEMA.loadout` in `replay.ts`) — distinct from the WIRE
 * `LOADOUT_SCHEMA` above (a client's untrusted, pre-resolution SELECTION,
 * capped at `MAX_LOADOUT_SIZE`). `resolveLoadout`'s `null`-loadout branch
 * returns a champion's FULL pool verbatim, which can exceed
 * `MAX_LOADOUT_SIZE` once a champion's pool grows past 12 (Afflictor,
 * Warrior as of #540 — both 13) — reusing `LOADOUT_SCHEMA` here would be a
 * latent bug: such a champion's null-loadout replay would fail its own
 * schema the moment it's built (silently dropping the replay at match-end,
 * since `ReplayWriter.writeTo`'s `strictValidate` throws on a self-check
 * failure). Bounded instead by the max a champion's pool can ever be
 * (`MAX_ABILITY_SLOT + 1`, mirroring `CHAMPION_SCHEMA.abilities`'s own
 * bound). Same uniqueness invariant as `LOADOUT_SCHEMA`; never `null` (the
 * writer always resolves to a concrete list before recording — see
 * `REPLAY_PLAYER_SCHEMA`'s doc comment).
 */
export const RESOLVED_LOADOUT_SCHEMA = z
  .array(ABILITY_ID_SCHEMA)
  .min(1)
  .max(MAX_ABILITY_SLOT + 1)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: "loadout must not contain duplicate ability ids",
  });

// ─── SimInput ──────────────────────────────────────────────────────────────
// Shared by client-input messages, replay entries, and (types-only) the sim.

/**
 * #252: WASD directional movement — held-key state, the ONE player movement
 * scheme (#563: click-to-move/follow-cursor's `moveTo`/`stop` wire kinds were
 * removed outright — WASD is the only supported movement input). Each of
 * the four flags is independent (diagonal holds, e.g. `forward && strafeRight`,
 * are legal) and the whole record REPLACES the entity's persisted movement
 * intent (last-writer-wins) — the client sends a fresh `moveDir` on every key
 * transition (down/up), not every tick. All-false is the natural "released
 * every key" state (a movement no-op). `forward`+`backward` (or
 * `strafeLeft`+`strafeRight`) held together cancel on that axis — see
 * `directionalDelta` in `@pit/sim`.
 */
/**
 * #351: WASD strafe axis convention — `"facing"` (default) resolves the four
 * flags relative to the caster's current FACING (today's behavior, byte-
 * identical). `"screen"` resolves them against the FIXED world axes the
 * isometric camera renders as up/down/left/right on screen, regardless of
 * facing. Wire-carried (not a client-local remap) because a screen-relative
 * player's strafe axes must be replay/determinism-identical across clients —
 * see `directionalDelta` in `@pit/sim` for the resolution math. Defaulted so
 * an old replay/golden `moveDir` record (pre-#351) parses byte-identically.
 */
export const STRAFE_MODE_SCHEMA = z.union([z.literal("facing"), z.literal("screen")]);

export type StrafeMode = z.infer<typeof STRAFE_MODE_SCHEMA>;

export const SIM_INPUT_MOVE_DIR_SCHEMA = z.strictObject({
  kind: z.literal("moveDir"),
  forward: z.boolean(),
  backward: z.boolean(),
  strafeLeft: z.boolean(),
  strafeRight: z.boolean(),
  strafeMode: STRAFE_MODE_SCHEMA.default("facing"),
});

/**
 * #565: the ONE canonical cursor/aim primitive — sent UNCONDITIONALLY every
 * tick by the client's single collector (`cursorTracker.ts`) whenever a
 * cursor position is known, independent of button state, movement mode, or
 * any toggle. Carries the world point the local player is currently aiming
 * at; the sim persists it verbatim onto `Entity.aimPoint` (last-writer-wins,
 * like `moveDir`) and derives `Entity.facing` from it as a PURE function each
 * tick wherever movement doesn't already drive facing (see `step.ts`'s
 * `applyAimPointFacing`) — replacing the retired `face` input (#252/#346),
 * which only updated facing on an explicit, gated send. `aimPoint` itself is
 * NOT movement — it never touches `moveIntent`. This is also the
 * field #559's cast-resolve targeting and #562's bot-aim decode read/write
 * (a bot's synthetic aim is a different PRODUCER of the same server-side
 * field, not a second field).
 */
export const SIM_INPUT_AIM_POINT_SCHEMA = z.strictObject({
  kind: z.literal("aimPoint"),
  x: UNITS_SCHEMA,
  y: UNITS_SCHEMA,
});

/**
 * #188: player-initiated CAST cancel. Distinct from `stop` (which clears
 * movement and deliberately does NOT touch a cast). Routes to `cancelCast`
 * (cast) / channel-end (a future channel) in the sim — a no-op when idle. A
 * separate input kind (Option 1) so a stop-move and a stop-cast are never
 * conflated on the wire.
 */
export const SIM_INPUT_STOP_CAST_SCHEMA = z.strictObject({
  kind: z.literal("stopCast"),
});

export const SIM_INPUT_CAST_SCHEMA = z.strictObject({
  kind: z.literal("cast"),
  slot: ABILITY_SLOT_SCHEMA,
  /**
   * Optional cursor world-point for skillshot (`targetType: "ground"`)
   * abilities (#36). Absent for lock-on casts, which resolve their aim from
   * the caster's target slot. A present `aim` on a lock-on ability is
   * harmless (ignored). The sim clamps this into map bounds BEFORE it enters
   * hashed cast state, so an out-of-range client value can't diverge Node vs
   * browser — client coords are feedback-only, server is authoritative.
   */
  aim: z.optional(z.strictObject({ x: UNITS_SCHEMA, y: UNITS_SCHEMA })),
});

export const SIM_INPUT_SET_TARGET_SCHEMA = z.strictObject({
  kind: z.literal("setTarget"),
  target: ENTITY_ID_SCHEMA,
});

/**
 * The two target slots every entity carries (dual targeting, #63): one
 * `hostile` (enemy-faction) and one `friendly` (ally-faction). This named
 * discriminant is the single source of truth for slot identity across the
 * wire, the sim (`clearTarget` routing), and the client input layer — so slot
 * names are never bare stringly-typed literals scattered around the codebase.
 * A `setTarget` needs no slot: the sim auto-routes it by the target's faction.
 */
export const TARGET_SLOT_SCHEMA = z.union([z.literal("hostile"), z.literal("friendly")]);

export type TargetSlot = z.infer<typeof TARGET_SLOT_SCHEMA>;

export const SIM_INPUT_CLEAR_TARGET_SCHEMA = z.strictObject({
  kind: z.literal("clearTarget"),
  slot: TARGET_SLOT_SCHEMA,
});

/**
 * #622: flip `Entity.autoAttackEnabled` (the #93 auto-attack on/off switch —
 * already hashed, already honored by the swing gate, but with no input that
 * could set it until now). A FLIP, not an explicit set — mirrors the locked
 * wire-input decision (spacebar toggles, default ON). Carries no payload:
 * there is exactly one bit to invert.
 */
export const SIM_INPUT_TOGGLE_AUTO_ATTACK_SCHEMA = z.strictObject({
  kind: z.literal("toggleAutoAttack"),
});

/**
 * #922: the universal CANCEL-YOUR-OWN-BUFF input — a player voluntarily drops
 * a self-held BUFF early (Ice Block released before it expires, a stance
 * switched off, a mobility buff ended). Before this arm there was NO way to
 * remove an aura from anyone on player command: every shipped `dispel` is
 * debuff removal, so this is a new capability, not a re-skin of one.
 *
 * Addressed by DEF ID, not by instance: `WIRE_AURA_SCHEMA` (below) carries no
 * unique instance handle — `{def, sourceSeat, appliedAtTick, ...}` is all the
 * client sees — so `def` is the only stable client-visible addressing key. The
 * sim drops the WHOLE aura (every matching instance, all stacks, see
 * `step.ts`'s `cancelOwnBuff`): "I want this buff off" means off, and a
 * per-stack cancel would need a stack count on the wire for no gameplay
 * benefit. This shape is expected to be FINAL — the only two plausible
 * extensions (per-instance addressing, per-stack cancel) are ruled out above.
 *
 * Three properties of the SIM side that this wire shape deliberately does not
 * encode (they are engine rules, not payload — see `step.ts`):
 *   - polarity-GATED: naming a DEBUFF is a silent no-op, which is what keeps
 *     CC in the game.
 *   - NOT gated by `canAct`: cancelling your own buff is not ACTING, so it
 *     works while stunned / silenced / feared / ice-blocked.
 *   - IGNORES `undispellable`: that flag means "an enemy purge cannot strip
 *     this", not "you may not let go of it". A future purge / spell-steal
 *     (a hostile `dispel`) MUST keep honoring it.
 *
 * A rejected cancel (debuff-named, def not held, expiring this tick) is a
 * SILENT no-op — same contract as `stopCast` when idle. No new error shape.
 */
export const SIM_INPUT_CANCEL_AURA_SCHEMA = z.strictObject({
  kind: z.literal("cancelAura"),
  def: AURA_DEF_ID_SCHEMA,
});

export const SIM_INPUT_SCHEMA = z.discriminatedUnion("kind", [
  // #563: the `moveTo`/`stop` arms (click-to-move / follow-cursor) are
  // REMOVED outright — WASD (`moveDir`) is the only supported movement
  // input scheme, same retirement precedent as #565's removal of the old
  // `face`/`aimSample` arms. No side-by-side period, per the issue's locked
  // decision.
  SIM_INPUT_STOP_CAST_SCHEMA,
  SIM_INPUT_CAST_SCHEMA,
  SIM_INPUT_SET_TARGET_SCHEMA,
  SIM_INPUT_CLEAR_TARGET_SCHEMA,
  // #252: additive WASD movement kinds — appended after the pre-existing
  // arms so a stored/replayed input's discriminant set only grows.
  SIM_INPUT_MOVE_DIR_SCHEMA,
  // #559: the #469 `aimSample` arm (window-gated cast-resolve aim sample) is
  // REMOVED outright (dead mechanism, superseded by #565's unconditional
  // `aimPoint` stream — same retirement precedent as #565's own removal of
  // the old `face` arm) — no side-by-side period, per the issue's locked
  // decision.
  // #565: the canonical cursor/aim primitive, appended after every
  // pre-existing arm.
  SIM_INPUT_AIM_POINT_SCHEMA,
  // #622: bindable auto-attack on/off toggle, appended after every
  // pre-existing arm.
  SIM_INPUT_TOGGLE_AUTO_ATTACK_SCHEMA,
  // #922: cancel one of your OWN buffs, appended after every pre-existing arm
  // (#252's precedent — the discriminant set only ever grows).
  SIM_INPUT_CANCEL_AURA_SCHEMA,
]);

export type SimInput = z.infer<typeof SIM_INPUT_SCHEMA>;

// ─── Client → Server messages ──────────────────────────────────────────────

export const CLIENT_HELLO_SCHEMA = z.strictObject({
  type: z.literal("hello"),
  protocolVersion: PROTOCOL_VERSION_SCHEMA,
  /**
   * #497: optional shared tester-access code, checked server-side via
   * `isTesterFlagged()` (`apps/server/src/testerFlag.ts`) against the
   * server's configured `testerAccessCode` (unset ⇒ everyone passes,
   * default-on). Not identity — there is no stable per-connection/per-user
   * id to attach an allowlist entry to (see the issue's design notes) — a
   * shared secret is the deliberate, minimal mechanism for this testing
   * phase. Omitted entirely when the local player has no code configured.
   */
  testerAccessCode: z.string().optional(),
  /**
   * #533: a previously-issued `auth_session` token (persisted client-side,
   * e.g. localStorage — see `apps/client/src/prefs.ts`'s `sessionToken`),
   * presented so a RETURNING connection (guest or signed-in) resolves back
   * to the SAME account instead of minting a fresh throwaway guest every
   * reconnect. Omitted (or no longer valid — expired/revoked/unknown) ⇒ the
   * server transparently mints a brand-new guest account + session and
   * returns its token on `welcome.sessionToken` (the frictionless-drop-in
   * default path — see `ws.ts`'s `hello` handler). Mirrors `testerAccessCode`
   * above's optional-field precedent.
   */
  sessionToken: z.string().min(1).max(128).optional(),
});

export type ClientHello = z.infer<typeof CLIENT_HELLO_SCHEMA>;

/**
 * #499/#676/#609: one non-creator seat's occupant plan for a practice-mode
 * `createMatch` — entries of `teammateSeats`/`opponentSeats` below.
 *   - `"bot"` — occupy the seat with the PINNED checkpoint `manifestId`
 *     names (an id from a prior `botList` response). Resolved server-side
 *     via `findBotManifestEntry` (an exact-id lookup) — NEVER re-resolved by
 *     champion+tier — so a saved/replayed practice config always lands on
 *     the EXACT checkpoint it was saved against, even after the manifest
 *     gains newer entries for the same champion/tier (see `botSeat.ts`).
 *   - `"human"` — explicitly leave the seat open for a human `joinMatch` —
 *     behaviorally identical to omitting the seat entirely, but lets a
 *     saved config record "vs. a human" as a real, explicit decision rather
 *     than relying on field absence.
 *   - `"dummy"` (#609) — a genuinely no-op controller (never emits input;
 *     `botSeat.ts`'s `addDummySeat`, `idleController`). Only valid as the
 *     SOLE entry of a 1v1 `opponentSeats` (never inside `teammateSeats` — a
 *     dummy teammate makes no sense; never combined with a 3v3
 *     `opponentSeats` — out of scope for #609).
 * A request carrying `teammateSeats` and/or `opponentSeats` is practice-
 * mode-SHAPED: `ws.ts` rejects it (before touching the room) unless
 * `isTesterFlagged()` is `true` for the sending connection, EXCEPT any SOLO
 * (`teamSize === 1`) request — one opponent seat of any kind, no teammates —
 * which is ungated (#609's dummy-only carve-out, widened by ISSUE-773 to
 * cover the 1v1-vs-bot practice screen). 3v3 practice is
 * server-enforced-gated, not merely UI-hidden (#497).
 */
export const OPPONENT_SEAT_SCHEMA = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("bot"),
    /** `bots/manifest.json` entry id (`BotManifestEntry.id`). Wire-level
     *  shape only (bounded non-empty string) — existence + promotion status
     *  are validated server-side against the loaded manifest. */
    manifestId: z.string().min(1).max(80),
  }),
  z.strictObject({ kind: z.literal("human") }),
  // #609: appended after the pre-existing arms (schemaHash union-order
  // sensitivity — see schemaHash.ts).
  z.strictObject({ kind: z.literal("dummy") }),
]);

export type OpponentSeat = z.infer<typeof OPPONENT_SEAT_SCHEMA>;

export const CLIENT_CREATE_MATCH_SCHEMA = z.strictObject({
  type: z.literal("createMatch"),
  /** Champion the creator plays. Validated against the content bundle's
   *  known ids server-side; unknown ids are rejected. */
  championId: CHAMPION_ID_SCHEMA,
  /** #323: the creator's chosen ability loadout. See `LOADOUT_SCHEMA`. */
  loadout: LOADOUT_SCHEMA,
  /**
   * #676: the OTHER seats on the creator's OWN team (3v3: 2 entries; 1v1:
   * omitted/empty). Replaces the pre-#676 singular `opponentSeat` field —
   * every existing non-practice caller (which never set that field) is
   * still byte-identical, since omitting BOTH this and `opponentSeats`
   * below is today's ordinary non-practice-mode behavior (seat 1 stays
   * open for a human `joinMatch`), zero back-compat risk. Never contains a
   * `"dummy"` entry (see `OPPONENT_SEAT_SCHEMA`'s doc comment). Bounded at
   * 2 — 3v3's only shipped non-creator teammate count; `ws.ts` additionally
   * requires `teammateSeats.length + 1` to be a KNOWN team size (1 or 3).
   */
  teammateSeats: z.array(OPPONENT_SEAT_SCHEMA).max(2).optional(),
  /**
   * #676 (was #499's singular `opponentSeat`): the OPPOSING team's seats —
   * 1 entry for 1v1 practice, 3 for 3v3 practice. `ws.ts` requires this
   * array's length to equal `teammateSeats.length + 1` exactly. See
   * See `OPPONENT_SEAT_SCHEMA`'s doc comment for the solo-1v1 tester-gate
   * carve-out.
   */
  opponentSeats: z.array(OPPONENT_SEAT_SCHEMA).max(6).optional(),
  /** #1011: asymmetric Practice Mode. With this field, the creator has one
   * human teammate and the opponent array contains exactly this many pinned
   * bot seats. The server validates the complete contract. */
  practiceBotCount: z.number().int().min(1).max(6).optional(),
  /**
   * #678: seat-0-only override of the room's map, by human-readable slug
   * (e.g. `"landscape"`) — resolved server-side to a concrete `PitMap.id`
   * ONCE at room-creation time via the content bundle's loaded maps
   * (`@pit/content`'s `mapBySlug`), never an opaque `MapId` on the wire. An
   * unknown slug is rejected (`badMessage`) before a room is created.
   * Omitted ⇒ today's ordinary behavior (the server's configured default
   * map) — byte-identical for every existing caller. A later joiner's own
   * map preference (there isn't one — `joinMatch` carries no such field) is
   * moot: only the room's FIRST seat can ever set this.
   */
  mapSlug: z.string().min(1).max(64).optional(),
});

export const CLIENT_JOIN_MATCH_SCHEMA = z.strictObject({
  type: z.literal("joinMatch"),
  matchId: MATCH_ID_SCHEMA,
  /** Champion the joiner plays. Validated server-side. */
  championId: CHAMPION_ID_SCHEMA,
  /** #323: the joiner's chosen ability loadout. See `LOADOUT_SCHEMA`. */
  loadout: LOADOUT_SCHEMA,
});

/**
 * Queue descriptor for matchmaking. Deliberately an OBJECT (not a bare
 * requiredSeats number) so future dimensions — region, playlist, party
 * size — are additive fields without a wire break. The server resolves
 * `mode` to concrete rules (required seats, team size, map) via its mode
 * registry; the wire never carries the resolved rules.
 *
 * `ranked` is present as a queue-partitioning key from day one (ranked and
 * unranked never share a room), but NO rating/matchmaking logic exists yet.
 */
export const QUEUE_SPEC_SCHEMA = z.strictObject({
  mode: MODE_ID_SCHEMA,
  ranked: z.boolean(),
});

export type QueueSpec = z.infer<typeof QUEUE_SPEC_SCHEMA>;

/**
 * Quick-join / matchmaking request. The server places the connection into
 * an open room for the requested queue (creating one if none is waiting).
 * Distinct from `createMatch` (explicit private room) and `joinMatch`
 * (explicit room by id) — both of which remain for private/share-link play.
 */
export const CLIENT_QUICK_JOIN_SCHEMA = z.strictObject({
  type: z.literal("quickJoin"),
  queue: QUEUE_SPEC_SCHEMA,
  /** Champion the player plays in the matched room. Validated server-side. */
  championId: CHAMPION_ID_SCHEMA,
  /** #323: the player's chosen ability loadout. See `LOADOUT_SCHEMA`. */
  loadout: LOADOUT_SCHEMA,
});

export const CLIENT_INPUT_SCHEMA = z.strictObject({
  type: z.literal("input"),
  seq: SEQ_SCHEMA,
  input: SIM_INPUT_SCHEMA,
});

/**
 * #332: explicit client→server "leave the room I'm currently attached to".
 * Covers both a still-`waiting` room (e.g. Quick Match's Cancel button —
 * the room this fixes) and an early voluntary leave mid-match (treated the
 * same as a disconnect: opponent wins by forfeit). No fields needed — the
 * server already knows the sender's room/seat from its own connection
 * state, exactly like every other control message. The server does not
 * reply with an ack; the requesting connection is simply detached (see
 * `ws.ts`'s `leaveMatch` handler), freeing it to `createMatch`/`joinMatch`/
 * `quickJoin` again immediately.
 */
export const CLIENT_LEAVE_MATCH_SCHEMA = z.strictObject({
  type: z.literal("leaveMatch"),
});

/**
 * #499: client→server request for the promoted, 1v1-eligible bot roster —
 * the data source for the practice-mode setup screen's bot-opponent picker.
 * No payload: the server always answers with its FULL eligible list
 * (today's manifest is tiny; a paginated/filterable query is a separate
 * future change once the ladder grows). Gated on `isTesterFlagged()` — a
 * non-flagged connection gets `error: unauthorized`, never a (possibly
 * empty) list, so the feature is genuinely gated server-side, not just
 * hidden client-side. NOTE (ISSUE-773): this gate was deliberately left
 * as-is when `createMatch`'s was widened to ungate solo (1v1) practice, so
 * on a deployment that actually sets a tester code a non-flagged client can
 * still create a bot 1v1 — but only from a `manifestId` it already knows.
 */
export const CLIENT_LIST_BOTS_SCHEMA = z.strictObject({
  type: z.literal("listBots"),
});

/**
 * #833: the length-capped chat text a player typed. Shared by the INBOUND
 * client message (`CLIENT_CHAT_SCHEMA`) and the OUTBOUND per-snapshot
 * broadcast (`WIRE_CHAT_MESSAGE_SCHEMA`), so the two can never drift — the
 * server re-broadcasts the exact string it accepted, and a message that
 * parses on the way in is guaranteed to parse on the way out.
 *
 * `.min(1)`: an empty message is rejected at the boundary rather than
 * broadcast as an empty floater. Whitespace-only text is NOT expressible as
 * a schema constraint here (a `.refine` is invisible to `SCHEMA_HASH` — see
 * `schemaHash.ts`), so it is rejected by the server handler instead; see
 * `ws.ts`'s `chat` case.
 *
 * See `MAX_CHAT_TEXT_LEN` in `limits.ts` for why the cap exists and why it
 * is 160.
 */
const CHAT_TEXT_SCHEMA = z.string().min(1).max(MAX_CHAT_TEXT_LEN);

/**
 * #833: client→server "say this in my current match". No seat field — the
 * server already knows the sender's room/seat from its own connection state,
 * exactly like `leaveMatch`, and trusting a client-supplied seat would let
 * anyone impersonate anyone. No `seq` either: chat is not a `SimInput`, never
 * enters the deterministic step, and is not replayed — so it needs neither
 * the input pipeline's ordering guarantees nor its rate cap (it short-
 * circuits as a `control` action; see `pipeline.ts`).
 *
 * FILTERED SERVER-SIDE (#855), not here: the wire schema still carries any
 * length-legal string, because a protocol schema is the wrong place for a
 * content policy — a `.refine`/`.regex` here would be invisible to
 * `SCHEMA_HASH` (see `schemaHash.ts`) yet would silently change what parses,
 * and rejecting the FRAME would cost the sender a malformed strike and tell
 * them exactly which words are filtered. `Room.enqueueChat` instead
 * sanitizes (control characters, bidi overrides, zero-width joiners), drops
 * hard-blocked slurs SILENTLY, and rate-limits per connection. The wire-level
 * backstops remain `MAX_CHAT_TEXT_LEN` (length) and `MAX_CHAT_PER_SNAPSHOT`
 * (volume).
 */
export const CLIENT_CHAT_SCHEMA = z.strictObject({
  type: z.literal("chat"),
  text: CHAT_TEXT_SCHEMA,
});

export const CLIENT_MESSAGE_SCHEMA = z.discriminatedUnion("type", [
  CLIENT_HELLO_SCHEMA,
  CLIENT_CREATE_MATCH_SCHEMA,
  CLIENT_JOIN_MATCH_SCHEMA,
  CLIENT_QUICK_JOIN_SCHEMA,
  CLIENT_INPUT_SCHEMA,
  // #332: appended after the pre-existing arms (schemaHash union-order
  // sensitivity — see schemaHash.ts).
  CLIENT_LEAVE_MATCH_SCHEMA,
  // #499: appended after the pre-existing arms (same union-order sensitivity).
  CLIENT_LIST_BOTS_SCHEMA,
  // #833: appended after the pre-existing arms (same union-order sensitivity).
  CLIENT_CHAT_SCHEMA,
]);

export type ClientMessage = z.infer<typeof CLIENT_MESSAGE_SCHEMA>;

// ─── Server → Client messages ──────────────────────────────────────────────

/** Exhaustive typed reason for `inputRejected`. */
export const INPUT_REJECT_REASON_SCHEMA = z.union([
  z.literal("malformed"),
  z.literal("notInMatch"),
  z.literal("wrongPhase"),
  z.literal("staleSeq"),
  z.literal("rateLimited"),
  z.literal("invalidTarget"),
  z.literal("invalidSlot"),
]);

export type InputRejectReason = z.infer<typeof INPUT_REJECT_REASON_SCHEMA>;

/** Exhaustive typed connection error code. Grows with WPs. */
export const ERROR_CODE_SCHEMA = z.union([
  z.literal("internal"),
  z.literal("protocolMismatch"),
  z.literal("badMessage"),
  z.literal("oversized"),
  z.literal("rateLimited"),
  z.literal("unauthorized"),
  /** ISSUE-646: a practice-mode bot opponent seat failed to attach AFTER
   *  `matchCreated`/`matchPhase: "waiting"` already went out (checkpoint
   *  load rejected — missing/corrupt `.onnx` file, missing native binding,
   *  etc.). Without this, the creator's client had already rendered
   *  "waiting for opponent" and had no way to learn the attach failed —
   *  an infinite, silent hang. See `attachPracticeBotSeat` in
   *  `apps/server/src/ws.ts`. */
  z.literal("botAttachFailed"),
]);

export type ErrorCode = z.infer<typeof ERROR_CODE_SCHEMA>;

/** Match phase. `winner` is only meaningful in `ended`; the schema forces
 * it to be present (and `null` in every other phase) so parsers don't have
 * to branch on the discriminant to know the shape. */
export const MATCH_PHASE_SCHEMA = z.union([
  z.literal("waiting"),
  z.literal("countdown"),
  z.literal("active"),
  z.literal("ended"),
]);

export type MatchPhase = z.infer<typeof MATCH_PHASE_SCHEMA>;

// ─── WireSimState (server → client snapshot payload) ────────────────────

const CAST_STATE_SCHEMA = z.union([
  z.null(),
  z.strictObject({
    slot: ABILITY_SLOT_SCHEMA,
    startTick: TICK_SCHEMA,
    endTick: TICK_SCHEMA,
    target: z.union([ENTITY_ID_SCHEMA, z.null()]),
    /**
     * #629: the resolved ground point for a point-aim (skillshot) cast —
     * the SAME `castState.aim` (`kind: "point"`) the sim itself already
     * hashes at cast-start, mirrored verbatim (never re-derived
     * client-side). `null` for an entity-aim cast (mirrors `target`'s own
     * null-for-the-other-case convention above) — `target`/`aimPoint` are
     * complementary, at most one is non-null. Lets a ground-targeted
     * ability (Meteor / Rain of Arrows / Shadowfury) anchor its indicator
     * at the real impact point instead of approximating with the
     * caster's own position (`abilityVisuals.ts`).
     */
    aimPoint: z.union([z.strictObject({ x: UNITS_SCHEMA, y: UNITS_SCHEMA }), z.null()]),
  }),
]);

/**
 * Live per-tick charge state (#84 mechanic, #466 wire exposure) for a
 * charge-based ability slot. `available` mirrors the sim's `ChargeSlot.
 * available` (0..max charges usable right now). `nextChargeReadyAtTick`
 * mirrors `ChargeSlot.nextReadyAt` — the tick the NEXT charge refills;
 * combined with the ability's static, content-derived `rechargeTicks`
 * (`abilityFacts.ts`, client-side), this is enough for the client to
 * compute the in-progress recharge sweep fraction the exact same way
 * `computeActionBarState` already combines `readyAtTick` + content-derived
 * `cooldownTicks` for an ordinary cooldown. `max` is NOT threaded here —
 * it's static content, resolved client-side via `abilityFacts.ts`'s
 * `maxCharges` (same convention as `resource`/`points`' `max` staying off
 * the wire, see their doc comments above).
 */
const COOLDOWN_CHARGE_STATE_SCHEMA = z.strictObject({
  available: z.number().int().gte(0),
  nextChargeReadyAtTick: TICK_SCHEMA,
});

export type CooldownChargeState = z.infer<typeof COOLDOWN_CHARGE_STATE_SCHEMA>;

const COOLDOWN_ENTRY_SCHEMA = z.strictObject({
  slot: ABILITY_SLOT_SCHEMA,
  readyAtTick: TICK_SCHEMA,
  /**
   * #466: live charge-pool state for a charge-based ability slot — `null`
   * for a slot whose ability has no `charges` block (the OVERWHELMING
   * majority of abilities), so this is additive: a non-charge ability's
   * entry is otherwise byte-identical to before this field existed.
   */
  charges: z.union([COOLDOWN_CHARGE_STATE_SCHEMA, z.null()]),
});

/**
 * Integer facing sector, `0..FACING_SECTORS - 1` inclusive. The sim's
 * canonical `Facing` type uses the same integer encoding and identical
 * semantics; the wire schema stays deliberately loose (integer range
 * check only) so bumping `FACING_SECTORS` in sim is a one-file change
 * on the sim side plus a matching bump of `MAX_FACING` here.
 *
 * See `packages/sim/src/facing.ts` for the sector layout and the
 * clockwise-from-North indexing.
 */
const FACING_SCHEMA = z.number().int().gte(0).lte(MAX_FACING);

export type Facing = z.infer<typeof FACING_SCHEMA>;

/**
 * Render-relevant aura instance fields (#30, PROTOCOL_VERSION 6). The
 * client needs every one of these to draw a debuff/buff row:
 *   • `def`           — content key → icon + display name.
 *   • `sourceSeat`    — who applied it (attribution / coloring).
 *   • `appliedAtTick` — start of the current instance (DoT tick pulse,
 *                       elapsed-duration rendering).
 *   • `expiresAtTick` — duration bar; `null` ⇒ permanent (no bar).
 *   • `stacks`        — stack-count badge (≥ 1).
 *   • `absorbRemaining` — shield-pool balance for absorbPool auras
 *                       (0 when the aura has no shield primitive).
 *   • `polarity`      — server-authored buff/debuff classification (#168):
 *                       the client colors the row (buff gold / debuff red),
 *                       splits the self-aura bar, and curates nameplates off
 *                       THIS field instead of a local mirror table. Resolved
 *                       server-side from the aura DEF (an `AuraInstance` carries
 *                       only the def id), so it reaches the client without the
 *                       client loading the content bundle.
 *
 * Unlike projectiles, an `AuraInstance` carries NO server-only secret
 * (no effect payload — behavior is resolved from the content def by id),
 * so the wire shape mirrors the full runtime instance. The `def` string
 * is validated as a bounded content key, matching the loose-string
 * convention already used for `projectileId`.
 */
const WIRE_AURA_SCHEMA = z.strictObject({
  def: AURA_DEF_ID_SCHEMA,
  sourceSeat: SEAT_INDEX_SCHEMA,
  appliedAtTick: TICK_SCHEMA,
  expiresAtTick: z.union([TICK_SCHEMA, z.null()]),
  stacks: z.number().int().gte(1),
  absorbRemaining: z.number().int().gte(0),
  // #168: buff/debuff polarity, server-authored on the aura def and surfaced
  // here so the client reads it off the wire (not a local mirror table).
  polarity: AURA_POLARITY_SCHEMA,
});

export type WireAura = z.infer<typeof WIRE_AURA_SCHEMA>;

const WIRE_ENTITY_SCHEMA = z.strictObject({
  entityId: ENTITY_ID_SCHEMA,
  seat: SEAT_INDEX_SCHEMA,
  team: TEAM_INDEX_SCHEMA,
  x: UNITS_SCHEMA,
  y: UNITS_SCHEMA,
  facing: FACING_SCHEMA,
  hp: HP_SCHEMA,
  maxHp: HP_SCHEMA,
  alive: z.boolean(),
  cast: CAST_STATE_SCHEMA,
  cooldowns: z.array(COOLDOWN_ENTRY_SCHEMA),
  gcdReadyAtTick: TICK_SCHEMA,
  targets: z.strictObject({
    hostile: z.union([ENTITY_ID_SCHEMA, z.null()]),
    friendly: z.union([ENTITY_ID_SCHEMA, z.null()]),
  }),
  /** Active auras (#30). Empty for aura-free entities. Order mirrors the
   *  sim's canonical aura ordering; the client treats it as a display set. */
  auras: z.array(WIRE_AURA_SCHEMA),
  /**
   * Castable resource pool balance (#89/#266) — current only. `null` for a
   * resource-less champion (mirrors `Entity.resource === null` in @pit/sim).
   * `max` is static content (`ChampionDef.resource.max`), NOT threaded over
   * the wire per-tick — the client resolves it from a small champion-keyed
   * table (see `apps/client/src/champions/resourceLabels.ts`), the same
   * pattern `abilityLabels.ts` uses for ability names/icons.
   */
  resource: z.union([z.number().int().gte(0), z.null()]),
  /**
   * Builder/spender point pool balance (#90/#266) — current only, e.g. combo
   * points. `null` for a point-less champion (mirrors `Entity.points ===
   * null`). `max` is resolved client-side the same way as `resource` above.
   */
  points: z.union([z.number().int().gte(0), z.null()]),
  /**
   * Live, aura-modified collision/body radius (#342) — the SAME
   * `getStat(entity, champion, "radius", tick, bundle)` value the sim itself
   * collides/targets against (a `statMod` on `"radius"`, e.g. Stand Behind
   * Me's +30%, changes this per-tick). Previously the client only ever drew
   * (and hit-tested) a fixed `DEFAULT_BODY_RADIUS_UNITS` constant, so a
   * size-changing buff was mechanically real but invisible/unclickable —
   * this field closes that gap. REQUIRED (this repo's no-`.optional()`
   * convention) — every entity has a base `champion.stats.radius`, so there
   * is no null case to model.
   */
  radius: UNITS_SCHEMA,
  /**
   * Content-key into `ContentBundle.champions` (#420) — mirrors the sim's own
   * `Entity.championId` verbatim. REQUIRED (this repo's no-`.optional()`
   * convention) — every entity spawns from a champion, so there is no
   * null/absent case to model, same as `radius` above.
   *
   * WHY: before #420 the wire snapshot carried per-slot cast/cooldown state
   * for EVERY entity but no way to resolve WHICH champion a non-viewer
   * entity is playing. `TargetFrame`/`FriendlyTargetFrame`'s cast-bar spell
   * name (and `UnitFrameStack`'s per-entity champion color, #419) had to
   * fall back to a hardcoded placeholder champion for anyone but the local
   * player (same bug class as #275's own-frame regression) — this field
   * closes that gap so the client resolves the ACTUAL champion for any
   * entity's ability names/icons/colors, not just its own.
   *
   * `championId` is fixed spawn config (NOT hashed sim state — see
   * `Entity.championId`'s doc comment in `@pit/sim`), so surfacing it here
   * is a pure wire-shape addition with no sim/hash/SIM_VERSION involvement.
   */
  championId: CHAMPION_ID_SCHEMA,
});

/**
 * Render-relevant projectile fields (#29). The effect payload + interception
 * flags stay server-side; the client only needs to DRAW in-flight projectiles
 * (position + travel direction + owner for coloring + ability for shape). Interpolated like
 * entities between snapshots.
 */
const WIRE_PROJECTILE_SCHEMA = z.strictObject({
  projectileId: z.string().min(1).max(64),
  /** Original ability identity, preserved through reflection. Null for untagged projectiles. */
  abilityId: z.nullable(ABILITY_ID_SCHEMA),
  ownerSeat: SEAT_INDEX_SCHEMA,
  ownerTeam: TEAM_INDEX_SCHEMA,
  x: UNITS_SCHEMA,
  y: UNITS_SCHEMA,
  dx: UNITS_SCHEMA,
  dy: UNITS_SCHEMA,
  radius: UNITS_SCHEMA,
});

export type WireProjectile = z.infer<typeof WIRE_PROJECTILE_SCHEMA>;

/**
 * One circular sub-obstacle of a wire wall — mirrors `@pit/sim`'s
 * `WallObstacle` (`{x, y, radius}`) field-for-field. These are the SAME
 * row-of-circles centers the sim already computes at spawn (`spawnWall`,
 * geometry decision D1) — no new geometry is derived for the wire, only
 * serialized.
 */
const WIRE_WALL_OBSTACLE_SCHEMA = z.strictObject({
  x: UNITS_SCHEMA,
  y: UNITS_SCHEMA,
  radius: UNITS_SCHEMA,
});

/**
 * Render-relevant dynamic-terrain wall fields (#488, consuming the #129
 * `spawnWall` subsystem). Unlike a map pillar (static content the client
 * already knows), a wall is RUNTIME-spawned and must be pushed to the client
 * every tick it's alive, then removed the instant it expires. Only what the
 * client needs to DRAW a placeholder obstacle and retire it is surfaced:
 *   • `wallId`       — stable per-instance key (mirrors `projectileId`),
 *                      lets the client reconcile create/update/remove exactly
 *                      like `renderProjectiles` already does.
 *   • `ownerSeat` / `ownerTeam` — attribution (future coloring, mirrors
 *                      `WIRE_PROJECTILE_SCHEMA`).
 *   • `obstacles`    — the row-of-circles collision geometry, already
 *                      resolved sim-side (`Wall.obstacles`); the client draws
 *                      each circle verbatim rather than re-deriving the
 *                      wall's placement math.
 *   • `expiresAtTick` — the sim's own inclusive last-alive tick
 *                      (`Wall.expiresAtTick`); the client can show a
 *                      despawn countdown, though a wall simply vanishes from
 *                      this array the tick after (mirrors projectile/aura
 *                      expiry — the client never guesses a lifetime).
 * Blocking flags/hazard payload are DELIBERATELY OMITTED: they don't change
 * how a placeholder wall renders (this issue draws every wall identically),
 * and they're not needed for the "did I get blocked" read (the sim's own
 * collision response — an entity simply failing to move through — already
 * communicates that visually via position). A future textured/team-tinted
 * wall render can widen this shape then.
 */
const WIRE_WALL_SCHEMA = z.strictObject({
  wallId: z.string().min(1).max(64),
  ownerSeat: SEAT_INDEX_SCHEMA,
  ownerTeam: TEAM_INDEX_SCHEMA,
  expiresAtTick: TICK_SCHEMA,
  obstacles: z.array(WIRE_WALL_OBSTACLE_SCHEMA).min(1),
});

export type WireWall = z.infer<typeof WIRE_WALL_SCHEMA>;

/**
 * Cosmetic combat event (#48, PROTOCOL_VERSION 7; #345 added `ccApplied`;
 * #463 added `ccRemoved`). Describes damage/heal/CC-application/CC-removal
 * that occurred THIS tick, for floating-combat-text rendering.
 *
 * EPHEMERAL + NON-HASHED: these are a human-facing readout of state changes
 * that are ALREADY reflected elsewhere (hp, or the target's `auras` array).
 * They are produced fresh each tick, carried on the snapshot, and discarded
 * by the client after rendering. The sim determinism hash does NOT include
 * them (see @pit/sim events.ts). A dropped or duplicated event is a cosmetic
 * glitch, never a desync.
 *
 *   • "damage" / "heal" — target / amount (actual hp delta, post-clamp, > 0)
 *     / source (attribution / future combat log). `damage` additionally
 *     carries `ability` (#795): WHICH ability is responsible, where `source`
 *     is only WHO. `null` whenever the hit has no originating ability at all
 *     — an auto-attack swing, a periodic DoT tick, a trigger-dispatched
 *     reflect, a wall/zone hazard — so the client's impact audio falls back
 *     to its generic cue instead of replaying some earlier ability's sound.
 *     Presentation-only, exactly like the rest of this union: the client
 *     resolves the id to a bundled `.mp3` entirely locally (#608's slug-keyed
 *     boundary — no sound data ever goes on the wire or into the sim).
 *   • "ccApplied" (#345) — target / ccClass (the hard-CC classification: one
 *     of `CC_CLASS_SCHEMA` EXCLUDING `"slow"` — a slow shows no floater, only
 *     the movement-speed effect) / source. Emitted when a stun/mez/fear/
 *     silence/disarm/root aura is NEWLY attached to `target` (not on a
 *     refresh/stack of an aura it already held), so the client can pop a
 *     "Stunned!"/"Rooted!"/... floater reusing the SAME floating-combat-text
 *     system as damage/heal (#48) — no new above-head label mechanism.
 *   • "ccRemoved" (#463) — the removal-side mirror of `ccApplied`, same
 *     shape (`target` / `ccClass` / `source`). Emitted the instant a
 *     hard-CC aura is DROPPED from `target` — natural duration expiry,
 *     break-on-damage-taken, break-on-deal-damage, or a `dispel` selector
 *     match — so the client can pop a "-Stun"/"-Root"/... floater. `source`
 *     is the CC's ORIGINAL APPLIER (`AuraInstance.sourceSeat`), the same
 *     attribution axis `ccApplied` uses — NOT whoever/whatever caused the
 *     removal (a dispel caster, or "nothing" for a natural expiry) — so
 *     `source` answers "who CC'd this target" consistently on both the
 *     onset and removal floater. Never emitted for `"slow"` (mirrors
 *     `ccApplied`'s own exclusion — see `hardCcClasses`), and never emitted
 *     on a refresh/re-stack (that is not a removal).
 *
 * A discriminated union (not one flat shape) because `ccApplied`/`ccRemoved`
 * carry no `amount` and `damage`/`heal` carry no `ccClass` — Zod/TS both
 * reject a mis-shaped event at the boundary instead of leaving
 * `ccClass`/`amount` silently optional on every kind.
 */
const WIRE_EVENT_DAMAGE_SCHEMA = z.strictObject({
  kind: z.literal("damage"),
  target: ENTITY_ID_SCHEMA,
  amount: z.number().int().gte(1),
  source: SEAT_INDEX_SCHEMA,
  /** #795: the ability responsible for this hit, or `null` when it has none
   *  (auto-attack / DoT tick / trigger reflect / environmental). Required +
   *  nullable, never `.optional()` — the no-optionals rule at the top of this
   *  file. See the union doc comment above. */
  ability: z.union([ABILITY_ID_SCHEMA, z.null()]),
});
const WIRE_EVENT_ABSORB_SCHEMA = z.strictObject({
  kind: z.literal("absorb"),
  target: ENTITY_ID_SCHEMA,
  amount: z.number().int().gte(1),
  source: SEAT_INDEX_SCHEMA,
});
const WIRE_EVENT_IMMUNE_SCHEMA = z.strictObject({
  kind: z.literal("immune"),
  target: ENTITY_ID_SCHEMA,
  source: SEAT_INDEX_SCHEMA,
});
const WIRE_EVENT_HEAL_SCHEMA = z.strictObject({
  kind: z.literal("heal"),
  target: ENTITY_ID_SCHEMA,
  amount: z.number().int().gte(1),
  source: SEAT_INDEX_SCHEMA,
});
/** #345: newly-applied hard-CC callout. `ccClass` reuses `CC_CLASS_SCHEMA`
 *  wholesale (all 7 values parse) — the server simply never emits `"slow"`
 *  here (see the `hardCcClasses` filter in `@pit/sim`'s `ccClasses.ts`). */
const WIRE_EVENT_CC_APPLIED_SCHEMA = z.strictObject({
  kind: z.literal("ccApplied"),
  target: ENTITY_ID_SCHEMA,
  ccClass: CC_CLASS_SCHEMA,
  source: SEAT_INDEX_SCHEMA,
});
/** #463: hard-CC removal callout — see the union doc comment above for the
 *  `source` attribution decision. Same `CC_CLASS_SCHEMA`-wholesale parse as
 *  `ccApplied`; the server never emits `"slow"` here either. */
const WIRE_EVENT_CC_REMOVED_SCHEMA = z.strictObject({
  kind: z.literal("ccRemoved"),
  target: ENTITY_ID_SCHEMA,
  ccClass: CC_CLASS_SCHEMA,
  source: SEAT_INDEX_SCHEMA,
});
const WIRE_EVENT_INTERRUPTED_SCHEMA = z.strictObject({
  kind: z.literal("interrupted"),
  target: ENTITY_ID_SCHEMA,
  source: SEAT_INDEX_SCHEMA,
});
const WIRE_EVENT_SCHEMA = z.discriminatedUnion("kind", [
  WIRE_EVENT_INTERRUPTED_SCHEMA,
  WIRE_EVENT_DAMAGE_SCHEMA,
  WIRE_EVENT_ABSORB_SCHEMA,
  WIRE_EVENT_IMMUNE_SCHEMA,
  WIRE_EVENT_HEAL_SCHEMA,
  WIRE_EVENT_CC_APPLIED_SCHEMA,
  WIRE_EVENT_CC_REMOVED_SCHEMA,
]);

export type WireEvent = z.infer<typeof WIRE_EVENT_SCHEMA>;

/**
 * Cosmetic CAST-FIRED signal (#804) — "seat X's ability in slot S resolved
 * during the step that produced this snapshot".
 *
 * WHY THIS EXISTS AT ALL. `WIRE_ENTITY_SCHEMA.cast` is persistent STATE: it
 * is non-null for exactly as long as the caster is non-idle, so a cast-time
 * ability (Meteor, `castTimeTicks: 30`) is observable across ~30 snapshots
 * and the client can edge-detect its onset. An INSTANT (`castTimeTicks: 0`,
 * or a cast collapsed to instant by #669's `nextCastInstant`) starts AND
 * resolves inside ONE `step` — `castState` is back to `idle` before the
 * snapshot is serialized — so `cast` is `null` in every snapshot that ever
 * existed and there is NO edge to detect. Frost Nova / Shadowfury / Cone of
 * Cold consequently never rendered their VFX at all (#804). A discrete
 * one-shot EVENT is the only shape that can carry a same-tick occurrence,
 * which is exactly the fresh-per-tick/drained/never-repeated contract
 * `WIRE_EVENT_SCHEMA` already has for combat text (#48).
 *
 * SEPARATE ARRAY, not a `WIRE_EVENT_SCHEMA` arm: every `WireEvent` describes
 * something that happened TO a `target` entity and is consumed by the
 * floating-combat-text/impact-audio drain (`CombatTextManager.ingest` spawns
 * one floater per event, capped/evicted). A cast fired BY a caster is the
 * other axis — folding it in would either spawn a bogus floater or force
 * every consumer of `events` to filter it out first.
 *
 * EPHEMERAL + NON-HASHED, exactly like `WireEvent`: produced fresh each tick
 * from the sim's cosmetic event sink, carried on one snapshot, discarded by
 * the client after rendering. A dropped/duplicated cast event is a cosmetic
 * glitch, never a desync (see `@pit/sim`'s `events.ts`).
 *
 * Fields:
 *   • `kind`      — `"castFired"`. Present so this stays a discriminated
 *                   shape as more cosmetic cast phases arrive (#657 wants
 *                   cast-start / cast-loop / impact); a second arm turns
 *                   this into a `z.discriminatedUnion` without touching any
 *                   consumer's narrowing.
 *   • `caster`    — the entity whose cast resolved (`EntityId`, so the client
 *                   joins straight against `entities`; the seat is readable
 *                   off that entity).
 *   • `slot`      — which action slot fired → the client resolves the
 *                   ability (and its VFX def / sound) from
 *                   `championId + slot`, exactly as the `cast`-edge path does.
 *   • `ability`   — the resolved ability's content id, or `null` for an
 *                   id-less (hand-built fixture) ability. Same
 *                   required-and-nullable convention as
 *                   `WIRE_EVENT_DAMAGE_SCHEMA.ability` (#795), and the field
 *                   #657 reads to pick a per-ability cast sound without
 *                   re-deriving the champion's slot table.
 *   • `startTick` — the tick the cast BEGAN (`castState.startTick`).
 *   • `firedTick` — the tick it RESOLVED. `startTick === firedTick` ⇔ the
 *                   cast never occupied a single serialized snapshot as
 *                   `cast` state, i.e. it is exactly the case the
 *                   `cast`-edge path structurally cannot see. That equality
 *                   is what lets the client add instants WITHOUT
 *                   double-spawning a cast-time ability's indicator (which
 *                   already fired at cast-START) — no cross-tick dedupe
 *                   bookkeeping, and no dependence on the snapshot-tick
 *                   convention. Both ticks are carried (rather than one
 *                   boolean) because #657 needs the real cast span for
 *                   cast-loop audio.
 *   • `aimPoint`  — the RESOLVED aim point (`aimX`/`aimY` at the commit site
 *                   in `@pit/sim`'s `advanceCasts`): the ground point for a
 *                   point-aim cast, or the target's position at resolve for
 *                   an entity-aim cast. Never null — every committed cast
 *                   has one — which is what lets a `"aimPoint"`-origin
 *                   instant (Shadowfury) anchor its circle correctly with no
 *                   `cast` state to read (#629 fixed the anchor; this makes
 *                   it observable).
 */
const WIRE_CAST_EVENT_SCHEMA = z.strictObject({
  kind: z.literal("castFired"),
  caster: ENTITY_ID_SCHEMA,
  slot: ABILITY_SLOT_SCHEMA,
  ability: z.union([ABILITY_ID_SCHEMA, z.null()]),
  startTick: TICK_SCHEMA,
  firedTick: TICK_SCHEMA,
  aimPoint: z.strictObject({ x: UNITS_SCHEMA, y: UNITS_SCHEMA }),
});

export type WireCastEvent = z.infer<typeof WIRE_CAST_EVENT_SCHEMA>;

const WIRE_MELEE_ATTACK_EVENT_SCHEMA = z.strictObject({
  kind: z.literal("meleeAttack"),
  caster: ENTITY_ID_SCHEMA,
});
export type WireMeleeAttackEvent = z.infer<typeof WIRE_MELEE_ATTACK_EVENT_SCHEMA>;

// Native contact telemetry preserves exact anchors, including range-expiry
// landings and effect origins outside arena bounds. Accept native i32 Units
// here without widening ordinary gameplay/content coordinate validation.
const PROJECTILE_CONTACT_UNITS_SCHEMA = brandedInt<Units>(
  "ProjectileContactUnits",
  -0x8000_0000,
  0x7fff_ffff,
);
export const WIRE_PROJECTILE_CONTACT_POINT_SCHEMA = z.strictObject({
  x: PROJECTILE_CONTACT_UNITS_SCHEMA,
  y: PROJECTILE_CONTACT_UNITS_SCHEMA,
});
export type WireProjectileContactPoint = z.infer<typeof WIRE_PROJECTILE_CONTACT_POINT_SCHEMA>;

export const WIRE_PROJECTILE_CONTACT_OUTCOME_SCHEMA = z.strictObject({
  target: ENTITY_ID_SCHEMA,
  damage: z.number().int().gte(0),
  absorbed: z.number().int().gte(0),
  // At least one effect was actually rejected; other effects may still land.
  immune: z.boolean(),
});
export type WireProjectileContactOutcome = z.infer<typeof WIRE_PROJECTILE_CONTACT_OUTCOME_SCHEMA>;

export const WIRE_PROJECTILE_CONTACT_KIND_SCHEMA = z.enum([
  "entity",
  "terrain",
  "rangeExpired",
  "zoneAbsorbed",
  "arenaBoundary",
]);
export type WireProjectileContactKind = z.infer<typeof WIRE_PROJECTILE_CONTACT_KIND_SCHEMA>;

export const WIRE_PROJECTILE_CONTINUATION_SCHEMA = z.enum([
  "ended",
  "pierced",
  "bounced",
  "reflected",
]);
export type WireProjectileContinuation = z.infer<typeof WIRE_PROJECTILE_CONTINUATION_SCHEMA>;

/**
 * Authoritative contact observation (#1275), separate from combat events.
 * Identity is (simulationTick, projectileId, contactIndex), scoped to a match.
 * simulationTick is the causal step, not the resulting snapshot tick. The
 * projectile need not survive into that snapshot. Owner fields describe the
 * incoming projectile; reflection changes ownership for subsequent contacts.
 *
 * contactPoint is the native contact anchor. effectOrigin is the actual AoE or
 * ground-zone center, when one was used; these points may intentionally differ.
 * Entity contacts set target, all other kinds use null. Outcomes are aggregated
 * by affected entity in ascending entity-ID order. Empty outcomes never imply
 * damage. These observations do not enter gameplay state, hashes or replays.
 */
export const WIRE_PROJECTILE_CONTACT_SCHEMA = z.strictObject({
  simulationTick: TICK_SCHEMA,
  contactIndex: z.number().int().gte(0),
  projectileId: z.string().min(1).max(64),
  abilityId: z.union([ABILITY_ID_SCHEMA, z.null()]),
  ownerSeat: SEAT_INDEX_SCHEMA,
  ownerTeam: TEAM_INDEX_SCHEMA,
  contactPoint: WIRE_PROJECTILE_CONTACT_POINT_SCHEMA,
  effectOrigin: z.union([WIRE_PROJECTILE_CONTACT_POINT_SCHEMA, z.null()]),
  kind: WIRE_PROJECTILE_CONTACT_KIND_SCHEMA,
  target: z.union([ENTITY_ID_SCHEMA, z.null()]),
  continuation: WIRE_PROJECTILE_CONTINUATION_SCHEMA,
  outcomes: z.array(WIRE_PROJECTILE_CONTACT_OUTCOME_SCHEMA),
});
export type WireProjectileContact = z.infer<typeof WIRE_PROJECTILE_CONTACT_SCHEMA>;

/**
 * In-game chat message (#833) — "seat X said this during the tick that
 * produced this snapshot".
 *
 * SEPARATE ARRAY, not a `WIRE_EVENT_SCHEMA` arm — the SAME reasoning
 * `WIRE_CAST_EVENT_SCHEMA` (#804) spells out above, applied verbatim: every
 * `WireEvent` describes something that happened TO a `target` entity and is
 * drained by the floating-combat-text AND impact-audio consumers. A chat line
 * is spoken BY a seat, has no target, and folding it into `events` would feed
 * it straight into `ImpactAudioManager.ingest` — pressing Enter would play a
 * damage sound. It gets its own top-level array for exactly the reason a cast
 * did.
 *
 * EPHEMERAL + NON-HASHED, exactly like `WireEvent`/`WireCastEvent`: the room
 * queues messages as they arrive, attaches them to the NEXT snapshot, and
 * clears the queue. Chat never enters `SimState`, is never hashed, is never
 * an input the sim consumes, and is NOT recorded in replays — a dropped or
 * duplicated chat line is a cosmetic glitch, never a desync. This is why
 * #833 is a PROTOCOL bump and NOT a `SIM_VERSION` bump.
 *
 * Fields:
 *   • `kind` — `"chat"`. Present for the same reason `castFired` keeps one:
 *              this stays a discriminated shape if v2 ever adds channels
 *              (team-only / whisper / system), so a second arm becomes a
 *              `z.discriminatedUnion` without touching any consumer's
 *              narrowing. v1 is ONE undifferentiated channel.
 *   • `seat` — the SPEAKING seat, stamped server-side from the sending
 *              connection's own seat (never client-supplied — see
 *              `CLIENT_CHAT_SCHEMA`). A `SeatIndex` rather than an
 *              `EntityId` because the speaker is a PLAYER, not a body: the
 *              message must still be attributable when that seat's entity is
 *              dead or absent from the viewer's filtered entity list, and
 *              the client resolves seat → entity itself for anchoring (and
 *              simply skips the floater when it can't).
 *   • `text` — the message, verbatim as accepted. See `CHAT_TEXT_SCHEMA`.
 */
const WIRE_CHAT_MESSAGE_SCHEMA = z.strictObject({
  kind: z.literal("chat"),
  seat: SEAT_INDEX_SCHEMA,
  text: CHAT_TEXT_SCHEMA,
});

export type WireChatMessage = z.infer<typeof WIRE_CHAT_MESSAGE_SCHEMA>;

export const WIRE_SIM_STATE_SCHEMA = z.strictObject({
  tick: TICK_SCHEMA,
  phase: MATCH_PHASE_SCHEMA,
  /** `winner` is only non-null when `phase === "ended"`. */
  winner: z.union([TEAM_INDEX_SCHEMA, z.null()]),
  entities: z.array(WIRE_ENTITY_SCHEMA),
  /** In-flight projectiles (#29). Empty on instant-only content. */
  projectiles: z.array(WIRE_PROJECTILE_SCHEMA),
  /** Active dynamic-terrain walls (#488, consuming #129's `spawnWall`
   *  subsystem). Empty until a wall-spawning ability resolves; an entry
   *  disappears the tick after its `expiresAtTick`, mirroring projectile
   *  expiry — the client never extrapolates a wall past its last snapshot. */
  walls: z.array(WIRE_WALL_SCHEMA),
  /** Cosmetic per-tick combat events (#48). Empty on quiet ticks. Non-hashed. */
  events: z.array(WIRE_EVENT_SCHEMA),
  /** Cosmetic per-tick cast-fired signals (#804) — one per cast that RESOLVED
   *  during the step that produced this snapshot. Empty on quiet ticks.
   *  Non-hashed. See `WIRE_CAST_EVENT_SCHEMA` for why this is a separate array
   *  from `events` and why an instant cast is unobservable without it. */
  castEvents: z.array(WIRE_CAST_EVENT_SCHEMA),
  meleeAttackEvents: z.array(WIRE_MELEE_ATTACK_EVENT_SCHEMA).optional(),
  /** Ephemeral per-step observations. Absent/empty means no contacts this tick. */
  projectileContacts: z.array(WIRE_PROJECTILE_CONTACT_SCHEMA).optional(),
  /** Chat messages spoken during the tick that produced this snapshot (#833).
   *  Empty on every quiet tick, which is nearly all of them. Non-hashed,
   *  never replayed. Bounded by `MAX_CHAT_PER_SNAPSHOT` — see
   *  `WIRE_CHAT_MESSAGE_SCHEMA` for why this is a separate array from
   *  `events` and why the speaker is a seat rather than an entity. */
  chat: z.array(WIRE_CHAT_MESSAGE_SCHEMA).max(MAX_CHAT_PER_SNAPSHOT),
});

export type WireSimState = z.infer<typeof WIRE_SIM_STATE_SCHEMA>;

// ─── Server messages ───────────────────────────────────────────────────────

export const SERVER_WELCOME_SCHEMA = z.strictObject({
  type: z.literal("welcome"),
  sessionId: z.string().min(1).max(64),
  protocolVersion: PROTOCOL_VERSION_SCHEMA,
  /**
   * #497: resolved result of `isTesterFlagged()` for THIS connection —
   * lets the client know definitively whether it's tester-flagged without
   * a second round-trip or protocol message. `true` for everyone while the
   * server's `testerAccessCode` is unset (default-on); otherwise `true`
   * only if this connection's `hello.testerAccessCode` matched.
   */
  testerFlagged: z.boolean(),
  /**
   * #533: the `@pit/data` account backing THIS connection — always
   * present, never null. Every connection has a real account from its
   * first `hello` onward: either resolved from `hello.sessionToken`, or a
   * transparently-created guest account when that token was absent/invalid
   * (see `ws.ts`'s `hello` handler and the issue's "frictionless drop-in
   * play, no anonymous-play path" locked decision).
   */
  accountId: z.string().min(1).max(64),
  /**
   * #533: a FRESH `auth_session` token, present only when this connection
   * minted a NEW session — i.e. `hello.sessionToken` was absent/invalid and
   * the server just created a guest account (or, in principle, any other
   * fresh-session case). `null` when the presented token was valid and
   * reused as-is (the client already holds it, nothing new to persist).
   * The client persists a non-null value (localStorage) so the NEXT
   * connection's `hello.sessionToken` resolves back to the SAME account —
   * this is what keeps a returning guest from fragmenting into a new
   * throwaway account every visit (see `apps/client/src/prefs.ts`).
   */
  sessionToken: z.union([z.string().min(1).max(128), z.null()]),
});

export const SERVER_MATCH_CREATED_SCHEMA = z.strictObject({
  type: z.literal("matchCreated"),
  matchId: MATCH_ID_SCHEMA,
  joinUrl: z.string().min(1).max(1024),
  // Seat assigned to THIS connection. Authoritative — the client no longer
  // guesses (0 for creator, 1 for joiner); quick-join needs it because the
  // player may land in either seat.
  seat: SEAT_INDEX_SCHEMA,
  /**
   * #700: the room's actual resolved map, by human-readable slug — mirrors
   * `CLIENT_CREATE_MATCH_SCHEMA.mapSlug`'s exact validation bounds. The
   * server resolves the room's map ONCE at creation time (`ws.ts`'s
   * `createMatch` handler, `mapBySlug`) and never mentioned it again on the
   * wire before this; the live-match renderer (`MatchScreen.tsx`) had no
   * way to learn which map a match was actually running on and always drew
   * the hardcoded `bare` geometry. Always present (the creator's own
   * requested slug, or the server's configured default when omitted) — the
   * same "matches ordinary behavior byte-for-byte" default `mapSlug`
   * request field already documents. Presentation-only: never touches the
   * sim/hash (mirrors `WIRE_ROSTER_ENTRY_SCHEMA`'s own framing).
   */
  mapSlug: z.string().min(1).max(64),
});

/**
 * One seat's champion selection (#244, PROTOCOL_VERSION 30). The client
 * otherwise only ever learns ITS OWN champion (from the local create/join/
 * quick-join selection) — every other seat is invisible client-side, so the
 * 3D-character path (#237) could only ever resolve the local player's model.
 * The server already tracks every seat's `championId` (`PlayerInfo`); this
 * mirrors it onto the wire so a full seat→champion roster reaches BOTH
 * clients.
 */
export const WIRE_ROSTER_ENTRY_SCHEMA = z.strictObject({
  seat: SEAT_INDEX_SCHEMA,
  championId: CHAMPION_ID_SCHEMA,
  /**
   * #732: which TEAM this seat fights for. The server has always known it
   * (`Room.addSeat` assigns `team = floor(seat / teamSize)` and stores it on
   * `PlayerInfo.team`) but never told the client: `teamSize` is server-internal
   * (it only ever reaches replay files), so the client could neither be told a
   * seat's team nor derive it. `WinnerBanner` consequently compared the
   * `matchPhase.winner` TEAM index against the local SEAT index — identical in
   * 1v1 (where team === seat), wrong for 5 of 6 seats in 3v3.
   *
   * Shipped as the AUTHORITATIVE value rather than shipping `teamSize` for the
   * client to re-derive: the moment team assignment stops being a pure function
   * of seat (uneven teams, spectators, 2v2v2, disconnect backfill, manual team
   * select) a client-side formula silently diverges, which is the bug class
   * this field exists to end.
   *
   * Present on EVERY `matchPhase` send (waiting/countdown/active/ended), so a
   * client that received any earlier roster still knows its team even if the
   * final `ended` send is missed. Presentation-only: never touches the sim/hash
   * (same framing as the rest of this entry), so no `SIM_VERSION` bump.
   */
  team: TEAM_INDEX_SCHEMA,
  /**
   * ISSUE-856: this seat's player-visible NAME, and the numeric discriminator
   * that disambiguates two players who chose the same one — rendered together
   * as `Shadow#0427`. Before this, a seat had no name on the wire at all: the
   * server carried a placeholder (`PlayerInfo.name`, "Player 1") that only
   * ever reached the replay file, so the client could not label an opponent
   * as anything but a seat index.
   *
   * ALWAYS PRESENT, for every seat kind. The `profile` row (and therefore the
   * name) is created at ACCOUNT creation for every provider — guest,
   * email/password, and a future `steam` — so there is no such thing as a
   * seated player without a name (ISSUE-856 scope 1). A bot/dummy seat, which
   * has no account at all, is given a generated name from the same curated
   * word list for the same reason a guest is: a seat whose NAME says "this
   * one isn't a real player" is a targeting signal.
   *
   * SERVER-AUTHORED, never client-supplied: the value comes from the
   * connection's own resolved profile, so a client cannot name itself (or
   * anyone else) on the wire. Content filtering, length and charset rules are
   * enforced where the name is WRITTEN (`POST /api/profile/name`), not here —
   * same reasoning as `CHAT_TEXT_SCHEMA`: a `.refine`/`.regex` policy here
   * would be invisible to `SCHEMA_HASH`. `MIN/MAX_DISPLAY_NAME_LEN` and
   * `DISPLAY_TAG_PATTERN` are the shape backstops, shared with the writer's
   * validator so a storable name is always a sendable one.
   *
   * Presentation-only: never touches the sim/hash (same framing as `team`
   * above), so no `SIM_VERSION` bump.
   */
  displayName: z.string().min(MIN_DISPLAY_NAME_LEN).max(MAX_DISPLAY_NAME_LEN),
  displayTag: z.string().regex(DISPLAY_TAG_PATTERN),
});

export type WireRosterEntry = z.infer<typeof WIRE_ROSTER_ENTRY_SCHEMA>;

/**
 * One seat's total damage dealt over the whole match (#471) — the post-match
 * damage-meter table's ONLY data source. `damageDealt` is a plain non-negative
 * integer total, server-accumulated off the existing EPHEMERAL per-tick
 * `damage` `CombatEvent` stream (see `@pit/sim`'s `events.ts` — those events
 * are deliberately never hashed/part of `SimState`). This is therefore NOT
 * hashed determinism state and needs no `SIM_VERSION` bump; it is pure
 * server-side bookkeeping mirrored onto the wire.
 */
export const WIRE_DAMAGE_ENTRY_SCHEMA = z.strictObject({
  seat: SEAT_INDEX_SCHEMA,
  damageDealt: z.number().int().gte(0),
});

export type WireDamageEntry = z.infer<typeof WIRE_DAMAGE_ENTRY_SCHEMA>;

export const SERVER_MATCH_PHASE_SCHEMA = z.strictObject({
  type: z.literal("matchPhase"),
  phase: MATCH_PHASE_SCHEMA,
  /** `winner` is non-null only when `phase === "ended"`. Kept required
   * (per no-optionals policy) — null in every other phase. */
  winner: z.union([TEAM_INDEX_SCHEMA, z.null()]),
  /**
   * Every seat attached to the room AT SEND TIME (#244), one entry per
   * joined player. Grows monotonically over successive `matchPhase` sends
   * within a match (`waiting` while seats are still filling → `countdown`
   * once the roster is complete) and is thereafter stable — presentation-
   * only, never touches the sim/hash.
   */
  roster: z.array(WIRE_ROSTER_ENTRY_SCHEMA),
  /**
   * The true countdown deadline (#276, PROTOCOL_VERSION 35) — the sim's own
   * `PhaseCountdown.endsAtTick` (`packages/sim/src/types.ts`), mirrored onto
   * this ONE-SHOT message verbatim. Non-null only when `phase === "countdown"`
   * (kept required + `null` elsewhere, per the `winner` no-optionals
   * convention above). Fixes the client fabricating its own deadline by
   * estimating `firstObservedTick + countdownTicks` (wrong for a
   * reconnect/late-join/spectate snapshot that isn't near tick 0) — the
   * client now reads the AUTHORITATIVE deadline directly instead of guessing
   * one.
   */
  endsAtTick: z.union([TICK_SCHEMA, z.null()]),
  /**
   * Total damage dealt per seat over the whole match (#471). Empty in every
   * phase except `ended` (same required-but-empty-elsewhere shape as
   * `endsAtTick` above) — the post-match damage-meter table's data source.
   * NOT hashed sim state (see `WIRE_DAMAGE_ENTRY_SCHEMA` doc comment above),
   * so this addition needs no `SIM_VERSION` bump.
   */
  damageDealt: z.array(WIRE_DAMAGE_ENTRY_SCHEMA),
  /**
   * #700: the room's actual resolved map, by human-readable slug — same
   * field/framing as `SERVER_MATCH_CREATED_SCHEMA.mapSlug`, mirrored onto
   * EVERY `matchPhase` send (`roster`'s exact precedent: presentation-only,
   * reaches every attached seat, never touches the sim/hash) so a seat that
   * never receives `matchCreated` (every `joinMatch` joiner — the creator-
   * only message) still learns the map it's actually playing on.
   */
  mapSlug: z.string().min(1).max(64),
});

export const SERVER_SNAPSHOT_SCHEMA = z.strictObject({
  type: z.literal("snapshot"),
  tick: TICK_SCHEMA,
  stateHash: STATE_HASH_SCHEMA,
  state: WIRE_SIM_STATE_SCHEMA,
});

/**
 * Delta / dirty-state update message (#1055).
 * Transmitted across active match ticks instead of re-serializing the full
 * WireSimState. Carries only the mutated fields and dynamic entities.
 */

export const WIRE_ENTITY_DELTA_SCHEMA = z.strictObject({
  entityId: ENTITY_ID_SCHEMA,
  seat: z.optional(SEAT_INDEX_SCHEMA),
  team: z.optional(TEAM_INDEX_SCHEMA),
  x: z.optional(UNITS_SCHEMA),
  y: z.optional(UNITS_SCHEMA),
  facing: z.optional(FACING_SCHEMA),
  hp: z.optional(HP_SCHEMA),
  maxHp: z.optional(HP_SCHEMA),
  alive: z.optional(z.boolean()),
  cast: z.optional(CAST_STATE_SCHEMA),
  cooldowns: z.optional(z.array(COOLDOWN_ENTRY_SCHEMA)),
  gcdReadyAtTick: z.optional(TICK_SCHEMA),
  targets: z.optional(
    z.strictObject({
      hostile: z.union([ENTITY_ID_SCHEMA, z.null()]),
      friendly: z.union([ENTITY_ID_SCHEMA, z.null()]),
    }),
  ),
  auras: z.optional(z.array(WIRE_AURA_SCHEMA)),
  resource: z.optional(z.union([z.number().int().gte(0), z.null()])),
  points: z.optional(z.union([z.number().int().gte(0), z.null()])),
  radius: z.optional(UNITS_SCHEMA),
  championId: z.optional(CHAMPION_ID_SCHEMA),
});

export type WireEntityDelta = z.infer<typeof WIRE_ENTITY_DELTA_SCHEMA>;

export const WIRE_PROJECTILES_DELTA_SCHEMA = z.strictObject({
  upserted: z.optional(z.array(WIRE_PROJECTILE_SCHEMA)),
  removed: z.optional(z.array(z.string().min(1).max(64))),
});

export type WireProjectilesDelta = z.infer<typeof WIRE_PROJECTILES_DELTA_SCHEMA>;

export const WIRE_WALLS_DELTA_SCHEMA = z.strictObject({
  upserted: z.optional(z.array(WIRE_WALL_SCHEMA)),
  removed: z.optional(z.array(z.string().min(1).max(64))),
});

export type WireWallsDelta = z.infer<typeof WIRE_WALLS_DELTA_SCHEMA>;

export const WIRE_ENTITIES_DELTA_SCHEMA = z.strictObject({
  upserted: z.optional(z.array(WIRE_ENTITY_DELTA_SCHEMA)),
  removed: z.optional(z.array(ENTITY_ID_SCHEMA)),
});

export type WireEntitiesDelta = z.infer<typeof WIRE_ENTITIES_DELTA_SCHEMA>;

export const SERVER_DELTA_SCHEMA = z.strictObject({
  type: z.literal("delta"),
  tick: TICK_SCHEMA,
  stateHash: STATE_HASH_SCHEMA,
  phase: z.optional(MATCH_PHASE_SCHEMA),
  winner: z.optional(z.union([TEAM_INDEX_SCHEMA, z.null()])),
  entities: z.optional(WIRE_ENTITIES_DELTA_SCHEMA),
  projectiles: z.optional(WIRE_PROJECTILES_DELTA_SCHEMA),
  walls: z.optional(WIRE_WALLS_DELTA_SCHEMA),
  events: z.optional(z.array(WIRE_EVENT_SCHEMA)),
  castEvents: z.optional(z.array(WIRE_CAST_EVENT_SCHEMA)),
  meleeAttackEvents: z.optional(z.array(WIRE_MELEE_ATTACK_EVENT_SCHEMA)),
  /** Replaces the preceding tick's contacts, including when absent/empty. */
  projectileContacts: z.optional(z.array(WIRE_PROJECTILE_CONTACT_SCHEMA)),
  chat: z.optional(z.array(WIRE_CHAT_MESSAGE_SCHEMA).max(MAX_CHAT_PER_SNAPSHOT)),
});

export type ServerDeltaMessage = z.infer<typeof SERVER_DELTA_SCHEMA>;

export const SERVER_INPUT_REJECTED_SCHEMA = z.strictObject({
  type: z.literal("inputRejected"),
  seq: SEQ_SCHEMA,
  reason: INPUT_REJECT_REASON_SCHEMA,
});

export const SERVER_PROTOCOL_MISMATCH_SCHEMA = z.strictObject({
  type: z.literal("protocolMismatch"),
  serverVersion: PROTOCOL_VERSION_SCHEMA,
});

export const SERVER_OPPONENT_CONNECTION_SCHEMA = z.strictObject({
  type: z.literal("opponentConnection"),
  connected: z.boolean(),
});

export const SERVER_ERROR_SCHEMA = z.strictObject({
  type: z.literal("error"),
  code: ERROR_CODE_SCHEMA,
});

/**
 * One PROMOTED, 1v1-eligible `bots/manifest.json` entry's client-safe
 * projection (#499) — deliberately narrow: `manifestId` is the stable pin a
 * saved practice config stores (see `OPPONENT_SEAT_SCHEMA`); `champion`/
 * `ratingTier` are what the picker UI displays/groups by. The entry's
 * `.onnx` path, file hash, and training provenance never leave the server.
 */
export const WIRE_BOT_ENTRY_SCHEMA = z.strictObject({
  manifestId: z.string().min(1).max(80),
  champion: CHAMPION_ID_SCHEMA,
  ratingTier: z.number().int().gte(1).lte(20),
});

export type WireBotEntry = z.infer<typeof WIRE_BOT_ENTRY_SCHEMA>;

/** Reply to `listBots` (#499). See `CLIENT_LIST_BOTS_SCHEMA`. */
export const SERVER_BOT_LIST_SCHEMA = z.strictObject({
  type: z.literal("botList"),
  bots: z.array(WIRE_BOT_ENTRY_SCHEMA),
});

/**
 * #742: ack for a successful `joinMatch` — the `matchCreated` analogue for
 * the OTHER way a client enters a match. Before this, `joinMatch`'s success
 * path sent nothing seat-specific back to the joiner at all (only the
 * broadcast `matchPhase.roster`, which has no "this entry is YOU" marker);
 * the client's `JoinScreen` papered over the gap with a hardcoded
 * `ownSeat: 1` guess ("seat 1 is the joiner convention"), which happens to
 * be right for a plain 1v1 join but is silently wrong for a 3v3 practice
 * match where a share-link joiner can land in any of seats 1–5 (filling
 * whichever open teammate/opponent seat is next in join order — see
 * `room.ts`'s `toSeatIndex(this.players.length)`). This mirrors
 * `SERVER_MATCH_CREATED_SCHEMA.seat`'s exact framing: the join-order-
 * assigned, authoritative seat for THIS connection.
 */
export const SERVER_MATCH_JOINED_SCHEMA = z.strictObject({
  type: z.literal("matchJoined"),
  matchId: MATCH_ID_SCHEMA,
  seat: SEAT_INDEX_SCHEMA,
});

export const SERVER_MESSAGE_SCHEMA = z.discriminatedUnion("type", [
  SERVER_WELCOME_SCHEMA,
  SERVER_MATCH_CREATED_SCHEMA,
  SERVER_MATCH_PHASE_SCHEMA,
  SERVER_SNAPSHOT_SCHEMA,
  SERVER_INPUT_REJECTED_SCHEMA,
  SERVER_PROTOCOL_MISMATCH_SCHEMA,
  SERVER_OPPONENT_CONNECTION_SCHEMA,
  SERVER_ERROR_SCHEMA,
  // #499: appended after the pre-existing arms (schemaHash union-order
  // sensitivity — see schemaHash.ts).
  SERVER_BOT_LIST_SCHEMA,
  // #742: appended after the pre-existing arms (same union-order
  // sensitivity).
  SERVER_MATCH_JOINED_SCHEMA,
  // #1055: delta update over TCP/WS.
  SERVER_DELTA_SCHEMA,
]);

export type ServerMessage = z.infer<typeof SERVER_MESSAGE_SCHEMA>;
